/**
 * GSAP Animations System — Medellin Gold District
 * Premium animation utilities powered by GSAP + ScrollTrigger
 * Respects prefers-reduced-motion
 */

const MGD_ANIM = (() => {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const DEFAULTS = {
    duration: 0.8,
    ease: 'power3.out',
    staggerAmount: 0.12,
  };

  const PRESETS = {
    'fade-up': { y: 40, opacity: 0 },
    'fade-down': { y: -40, opacity: 0 },
    'fade-in': { opacity: 0 },
    'fade-left': { x: 60, opacity: 0 },
    'fade-right': { x: -60, opacity: 0 },
    'scale-in': { scale: 0.92, opacity: 0 },
    'scale-up': { scale: 0.8, opacity: 0 },
    'clip-up': { clipPath: 'inset(100% 0 0 0)', opacity: 0 },
    'clip-down': { clipPath: 'inset(0 0 100% 0)', opacity: 0 },
    'blur-in': { filter: 'blur(12px)', opacity: 0 },
  };

  /**
   * Initialize scroll-triggered reveal animations
   * Finds all [data-animate] elements and sets up ScrollTrigger
   */
  function initScrollReveals() {
    if (REDUCED_MOTION) return;
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    const elements = document.querySelectorAll('[data-animate]:not([data-animate-init])');

    elements.forEach((el) => {
      const preset = el.dataset.animate;
      const fromVars = PRESETS[preset];
      if (!fromVars) return;

      el.setAttribute('data-animate-init', 'true');

      const delay = parseFloat(el.dataset.animateDelay) || 0;
      const duration = parseFloat(el.dataset.animateDuration) || DEFAULTS.duration;
      const triggerStart = el.dataset.animateStart || 'top 88%';

      gsap.set(el, fromVars);

      const toVars = {
        ...Object.fromEntries(
          Object.keys(fromVars).map((key) => {
            if (key === 'opacity') return [key, 1];
            if (key === 'y' || key === 'x') return [key, 0];
            if (key === 'scale') return [key, 1];
            if (key === 'filter') return [key, 'blur(0px)'];
            if (key === 'clipPath') return [key, 'inset(0% 0 0 0)'];
            return [key, null];
          })
        ),
        duration,
        delay,
        ease: DEFAULTS.ease,
        scrollTrigger: {
          trigger: el,
          start: triggerStart,
          once: true,
        },
      };

      gsap.to(el, toVars);
    });
  }

  /**
   * Stagger animate a group of children
   * @param {string|Element} container - Container selector or element
   * @param {string} childSelector - Children to animate
   * @param {string} preset - Animation preset name
   */
  function staggerReveal() {
    return;
  }

  /**
   * Parallax effect for background elements
   * @param {string|Element} el - Element selector
   * @param {number} speed - Parallax speed multiplier (default 0.3)
   */
  function parallax() {
    return;
  }

  function magneticButtons() {
    return;
  }

  /**
   * Text split and reveal animation
   * @param {string|Element} el - Target element
   * @param {string} type - 'chars' | 'words' | 'lines'
   */
  function textReveal(el, type = 'words') {
    if (REDUCED_MOTION) return;
    if (typeof gsap === 'undefined') return;

    const element = typeof el === 'string' ? document.querySelector(el) : el;
    if (!element) return;

    const text = element.textContent;
    let items;

    if (type === 'words') {
      const words = text.split(/\s+/).filter(Boolean);
      element.innerHTML = words.map((w) => `<span class="mgd-word" style="display:inline-block;overflow:hidden"><span class="mgd-word-inner" style="display:inline-block">${w}</span></span>`).join(' ');
      items = element.querySelectorAll('.mgd-word-inner');
    } else if (type === 'chars') {
      const chars = text.split('');
      element.innerHTML = chars.map((c) => c === ' ' ? ' ' : `<span class="mgd-char" style="display:inline-block;overflow:hidden"><span class="mgd-char-inner" style="display:inline-block">${c}</span></span>`).join('');
      items = element.querySelectorAll('.mgd-char-inner');
    }

    if (!items || !items.length) return;

    gsap.set(items, { y: '110%', opacity: 0 });

    gsap.to(items, {
      y: '0%',
      opacity: 1,
      duration: 0.7,
      ease: 'power3.out',
      stagger: type === 'chars' ? 0.03 : 0.06,
      scrollTrigger: {
        trigger: element,
        start: 'top 85%',
        once: true,
      },
    });
  }

  /**
   * Smooth counter animation for numbers
   * @param {Element} el - Element containing the number
   * @param {number} endValue - Target value
   * @param {number} duration - Animation duration
   */
  function animateCounter(el, endValue, duration = 1.2) {
    if (REDUCED_MOTION) {
      el.textContent = endValue;
      return;
    }
    if (typeof gsap === 'undefined') return;

    const obj = { value: 0 };
    gsap.to(obj, {
      value: endValue,
      duration,
      ease: 'power2.out',
      onUpdate: () => {
        el.textContent = Math.round(obj.value);
      },
    });
  }

  /**
   * Image hover zoom effect
   * @param {string} selector - Container elements with images
   */
  function imageHoverZoom() {
    return;
  }

  function initMegaMenu() {
    return;
  }

  function localizeSpanish() {}
  function formatCOP() {}

  function refresh() {
    initScrollReveals();
    if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
  }

  return {
    init: initScrollReveals,
    stagger: staggerReveal,
    parallax,
    magnetic: magneticButtons,
    textReveal,
    counter: animateCounter,
    imageZoom: imageHoverZoom,
    megaMenu: initMegaMenu,
    localize: localizeSpanish,
    formatCOP: formatCOP,
    refresh,
    PRESETS,
    DEFAULTS,
    REDUCED_MOTION,
  };
})();

// Export for module usage
if (typeof module !== 'undefined') {
  module.exports = MGD_ANIM;
}
